import { useQuery } from "@tanstack/react-query";
import { ArrowDownCircle, RefreshCw } from "lucide-react";
import { useEffect, useState } from "react";
import type { UpdateStatus } from "@ide/shared/desktop";
import { Button } from "@/components/ui/button";
import { InfinityMark } from "@/components/ui/infinity-loader";
import { Dialog, DialogContent, Popover, PopoverContent, PopoverTrigger } from "@/components/ui/overlay";
import { get } from "@/lib/api";
import { cn } from "@/lib/utils";
import { desktop } from "./bridge";

interface LatestRelease {
  version: string;
  notes: string | null;
  mandatory: boolean;
}

const platform = desktop?.platform === "darwin" ? "mac" : desktop?.platform === "linux" ? "linux" : "windows";

function useUpdate() {
  const [status, setStatus] = useState<UpdateStatus | null>(null);
  useEffect(() => {
    if (!desktop?.update) return;
    void desktop.update.status().then(setStatus);
    return desktop.update.onStatus(setStatus);
  }, []);
  return status;
}

function Body({ status, release }: { status: UpdateStatus; release: LatestRelease | null }) {
  const version = status.version ?? release?.version;
  return (
    <div className="space-y-3">
      {release?.notes ? (
        <div>
          <p className="mb-1 text-[11.5px] font-medium uppercase tracking-[0.06em] text-fg-subtle">
            Novidades
          </p>
          <p className="max-h-48 overflow-y-auto whitespace-pre-line text-[12.5px] leading-relaxed text-fg-muted">
            {release.notes}
          </p>
        </div>
      ) : null}
      {status.state === "downloading" ? (
        <div>
          <div className="flex justify-between text-[12px] text-fg-muted">
            <span>Baixando e conferindo o instalador…</span>
            <span className="tabular-nums">{status.percent ?? 0}%</span>
          </div>
          <div
            className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-bg-emphasis"
            role="progressbar"
            aria-valuenow={status.percent ?? 0}
          >
            <div className="h-full bg-fg transition-[width]" style={{ width: `${status.percent ?? 0}%` }} />
          </div>
        </div>
      ) : null}
      {status.state === "error" ? (
        <p className="text-[12px] text-[var(--danger)]">
          Não deu para baixar: {status.error}. Tente de novo.
        </p>
      ) : null}
      <p className="text-[11.5px] text-fg-subtle">
        O Kompil reinicia sozinho ao terminar. Suas edições já estão salvas no servidor (e na sua máquina, se
        a cópia local estiver ligada).
      </p>
      <Button
        variant="primary"
        className="w-full"
        loading={status.state === "downloading"}
        onClick={() => void desktop?.update.install()}
      >
        {status.state === "ready" ? (
          <RefreshCw className="h-3.5 w-3.5" />
        ) : (
          <ArrowDownCircle className="h-3.5 w-3.5" />
        )}
        {status.state === "ready" ? "Reiniciar e instalar" : `Atualizar para ${version ?? "a versão nova"}`}
      </Button>
    </div>
  );
}

/** O app vai fechar para instalar: tela cheia com o símbolo se formando (a janela do sistema segue). */
function Restarting({ version }: { version: string | null }) {
  return (
    <div
      role="alertdialog"
      aria-label="Atualizando o Kompil"
      className="fixed inset-0 z-[200] flex animate-fade-in flex-col items-center justify-center gap-5 bg-canvas/95 backdrop-blur-sm"
    >
      <InfinityMark className="h-[60px] w-[120px] text-fg" label={null} />
      <div className="text-center">
        <p className="text-[17px] font-semibold tracking-[-0.01em] text-fg">Atualizando o Kompil</p>
        <p className="mt-1 text-[13px] text-fg-muted">
          {version ? `Instalando a versão ${version}. ` : ""}O Kompil fecha e abre sozinho em seguida.
        </p>
      </div>
    </div>
  );
}

/**
 * Barra de título: versão nova publicada pelo painel. Clique em "Atualizar": baixa, confere o
 * SHA-512 e reinicia instalando. Versão obrigatória: um aviso que não sai até atualizar.
 */
export function UpdateNotice() {
  const status = useUpdate();
  const pending = status && ["available", "downloading", "ready", "error"].includes(status.state);
  const { data } = useQuery({
    queryKey: ["release", "latest", platform],
    queryFn: () => get<{ release: LatestRelease | null }>(`/releases/latest?platform=${platform}`),
    enabled: Boolean(pending),
    staleTime: 10 * 60_000,
  });
  if (status?.state === "installing") return <Restarting version={status.version} />;
  if (!status || !pending) return null;
  const release = data?.release ?? null;
  const version = status.version ?? release?.version ?? "";
  if (release?.mandatory && release.version === status.version) {
    return (
      <Dialog open>
        <DialogContent
          title={`Atualização obrigatória: Kompil ${version}`}
          description="Esta versão corrige pontos importantes. Atualize para continuar."
        >
          <Body status={status} release={release} />
        </DialogContent>
      </Dialog>
    );
  }
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            "flex h-7 items-center gap-1.5 rounded-md border border-border-strong px-2 text-[12px] font-medium text-fg outline-none transition-colors hover:bg-bg-muted focus-visible:ring-2 focus-visible:ring-ring/60",
          )}
          aria-label={`Atualização disponível: Kompil ${version}`}
        >
          <span className="relative flex h-2 w-2">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-fg opacity-40" />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-fg" />
          </span>
          {status.state === "downloading" ? `Atualizando ${status.percent ?? 0}%` : "Atualizar"}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[340px] p-4">
        <p className="mb-3 text-[14px] font-semibold text-fg">Kompil {version} disponível</p>
        <Body status={status} release={release} />
      </PopoverContent>
    </Popover>
  );
}
